import type { FieldPath } from "react-hook-form";

import type {
  TravelRuleFieldOption,
  TravelRuleRequiredField,
  TravelRuleValidationError,
} from "~/v2/api/rest/cryptoWallet";
import type { TravelRuleFormValues } from "./schema";

// RHF-typed wire path. Narrowed at the parser boundary (`toRequestedField`)
// so downstream `<Controller name={field.path} />` typechecks without casts.
export type RequestedFieldPath = FieldPath<TravelRuleFormValues>;

export interface ForcedField {
  field: string;
  valueType: string;
  rawValue: string;
}

export interface AddressMatchModalState {
  forcedFields: ForcedField[];
  vaspName: string;
  vaspDid: string;
}

// Flattened representation of a single backend-requested field. REQUIRED actions
// and ONE_OF branches both produce this shape; `branchId` groups ONE_OF fields
// for the renderer's "pick one" radio.
export interface RequestedField {
  /** Matches the RHF name verbatim, e.g. "beneficiary_given_name". */
  path: RequestedFieldPath;
  /** Backend-supplied per-field description; renderer uses it as label/help. */
  description: string;
  /** Raw `value_type` from the backend ("string", "bool", etc.). */
  valueType: string;
  /** Multi-option enum (>1). Single-option fields are routed to the modal. */
  options?: TravelRuleFieldOption[];
  /** Stable id for ONE_OF grouping; absent means REQUIRED (always submit). */
  branchId?: string;
  /** Optional branch header; rendered above the branch's inputs. */
  branchLabel?: string;
}

export interface TravelRuleRequest {
  /** Backend's top-level `message`, suitable for a banner above the fields. */
  banner: string;
  fields: RequestedField[];
}

export function coerceOptionValue(
  rawValue: string,
  valueType: string,
): string | boolean | number {
  switch (valueType.toLowerCase()) {
    case "bool":
    case "boolean":
      return rawValue === "true";
    case "number":
    case "int":
    case "integer":
      return Number(rawValue);
    default:
      return rawValue;
  }
}

export function isFilled(value: unknown): boolean {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "boolean") {
    return true;
  }
  if (value == null) {
    return false;
  }
  return true;
}

/**
 * Extract the VASP name and DID from an ADDRESS_BELONGS_TO_VASP message
 * ("...belongs to <Name> (<did:...>)"). Falls back to the
 * `beneficiary_vasp_id` forced option for the DID and a generic name label.
 */
export function parseVaspMatch(
  validationError: TravelRuleValidationError,
  forcedFields: ForcedField[],
): { vaspName: string; vaspDid: string } {
  const match = validationError.message.match(
    /belongs to (.+?) \((did:[^)]+)\)/,
  );
  const vaspIdField = forcedFields.find(
    (f) => f.field === "beneficiary_vasp_id",
  );
  return {
    vaspName: match?.[1] ?? "the verified source",
    vaspDid: match?.[2] ?? vaspIdField?.rawValue ?? "",
  };
}

const toRequestedField = (
  field: TravelRuleRequiredField,
  branch?: { id: string; label?: string },
): RequestedField => ({
  path: field.field as RequestedFieldPath,
  description: field.description,
  valueType: field.value_type,
  options: field.options,
  branchId: branch?.id,
  branchLabel: branch?.label,
});

/**
 * Flatten a TravelRule validation error into `RequestedField[]` for
 * `RequestedFieldsPanel`. Every requested field surfaces verbatim except
 * single-option forced values (`options.length === 1`), which route to
 * `AddressMatchModal`. The panel is the single source of truth for backend
 * requested fields — the main form hides duplicates via `panelClaimedPaths`.
 */
export function normalizeRequest(
  error: TravelRuleValidationError | null,
): RequestedField[] {
  if (!error) {
    return [];
  }

  const isFieldKept = (field: TravelRuleRequiredField): boolean =>
    !(field.options && field.options.length === 1);

  const out: RequestedField[] = [];
  for (const action of error.next_actions) {
    if (action.type === "ONE_OF") {
      const keptFields = action.required_fields.filter(isFieldKept);
      if (keptFields.length === 0) {
        continue;
      }
      const branch = { id: action.name, label: action.description };
      for (const field of keptFields) {
        out.push(toRequestedField(field, branch));
      }
    } else {
      for (const field of action.required_fields) {
        if (isFieldKept(field)) {
          out.push(toRequestedField(field));
        }
      }
    }
  }
  return out;
}

/**
 * Pull `options.length === 1` fields for AddressMatchModal to present as
 * forced values. Non-forced fields go through `normalizeRequest` to the panel.
 */
export function collectForcedFields(
  error: TravelRuleValidationError,
): ForcedField[] {
  const forced: ForcedField[] = [];
  for (const action of error.next_actions) {
    for (const field of action.required_fields) {
      if (field.options && field.options.length === 1) {
        forced.push({
          field: field.field,
          valueType: field.value_type,
          rawValue: field.options[0].value,
        });
      }
    }
  }
  return forced;
}
